import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { useToast } from '@/hooks/use-toast';

interface VariableMapping {
  id?: number;
  template_name: string;
  variable_index: number;
  database_column: string;
  connection_id?: number; // 🆕 NOVO: ID da conexão
  is_active: boolean;
}

interface WhatsAppConnection {
  id: number;
  name: string;
  phone_id: string;
  connection_type: string;
}

interface TemplateVariableMappingProps {
  clientId?: number;
}

export default function TemplateVariableMapping({ clientId }: TemplateVariableMappingProps) {
  const { toast } = useToast();
  const [mappings, setMappings] = useState<VariableMapping[]>([]);
  const [connections, setConnections] = useState<WhatsAppConnection[]>([]);
  const [validColumns, setValidColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [templateName, setTemplateName] = useState('');
  const [variableIndex, setVariableIndex] = useState('');
  const [databaseColumn, setDatabaseColumn] = useState('');
  const [connectionId, setConnectionId] = useState(''); // 🆕 NOVO: ID da conexão

  useEffect(() => {
    if (clientId) {
      loadConnections();
      loadMappings();
      loadValidColumns();
    }
  }, [clientId]);

  const loadConnections = async () => {
    try {
      const response = await fetch(`/api/whatsapp/client/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnections(data.data || []);
      } else {
        console.error('Erro ao carregar conexões');
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    }
  };

  const loadMappings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/template-variable-mappings/client/${clientId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setMappings(data.data || []);
      } else {
        console.error('Erro ao carregar mapeamentos');
      }
    } catch (error) {
      console.error('Erro ao carregar mapeamentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadValidColumns = async () => {
    try {
      const response = await fetch('/api/template-variable-mappings/columns/valid', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setValidColumns(data.data || []);
      } else {
        // Fallback para colunas padrão
        setValidColumns([
          'nome_associado',
          'placa',
          'modelo_veiculo',
          'valor_boleto',
          'data_vencimento',
          'celular',
          'codigo_associado',
          'codigo_veiculo',
          'codigo_regional',
          'nosso_numero',
          'tipo_envio',
          'pix_copia_cola',
          'linha_digitavel',
          'link_boleto',
          'cliente_nome_fantasia',
          'cliente_cnpj',
          'telefone_destino'
        ]);
      }
    } catch (error) {
      console.error('Erro ao carregar colunas válidas:', error);
    }
  };

  const handleSaveMapping = async () => {
    if (!templateName || !variableIndex || !databaseColumn || !connectionId) {
      toast({
        title: "Erro",
        description: "Todos os campos são obrigatórios, incluindo a conexão",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(true);
      const response = await fetch('/api/template-variable-mappings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          template_name: templateName,
          variable_index: parseInt(variableIndex),
          database_column: databaseColumn,
          connection_id: parseInt(connectionId), // 🆕 NOVO: Incluir connection_id
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Sucesso",
          description: data.created ? "Mapeamento criado com sucesso!" : "Mapeamento atualizado com sucesso!",
        });
        
        // Limpar formulário
        setTemplateName('');
        setVariableIndex('');
        setDatabaseColumn('');
        setConnectionId('');
        
        // Recarregar mapeamentos
        await loadMappings();
      } else {
        const error = await response.json();
        toast({
          title: "Erro",
          description: error.error || "Erro ao salvar mapeamento",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erro ao salvar mapeamento:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar mapeamento",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (mappingId: number) => {
    if (!confirm('Tem certeza que deseja excluir este mapeamento?')) {
      return;
    }

    try {
      const response = await fetch(`/api/template-variable-mappings/${mappingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        toast({
          title: "Sucesso",
          description: "Mapeamento excluído com sucesso!",
        });
        await loadMappings();
      } else {
        toast({
          title: "Erro",
          description: "Erro ao excluir mapeamento",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Erro ao excluir mapeamento:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir mapeamento",
        variant: "destructive",
      });
    }
  };

  const groupedMappings = mappings.reduce((acc, mapping) => {
    if (!acc[mapping.template_name]) {
      acc[mapping.template_name] = [];
    }
    acc[mapping.template_name].push(mapping);
    return acc;
  }, {} as Record<string, VariableMapping[]>);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Carregando mapeamentos...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Formulário para adicionar mapeamento */}
      <Card>
        <CardHeader>
          <CardTitle>Adicionar Mapeamento de Variável</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="template-name">Nome do Template</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Ex: boleto_lembrete"
              />
            </div>
            
            <div>
              <Label htmlFor="variable-index">Índice da Variável</Label>
              <Input
                id="variable-index"
                type="number"
                value={variableIndex}
                onChange={(e) => setVariableIndex(e.target.value)}
                placeholder="Ex: 1, 2, 3..."
                min="1"
              />
            </div>
            
            <div>
              <Label htmlFor="connection-id">Conexão WhatsApp</Label>
              <Select value={connectionId} onValueChange={setConnectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma conexão" />
                </SelectTrigger>
                <SelectContent>
                  {connections.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id.toString()}>
                      {connection.name} ({connection.connection_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="database-column">Coluna do Banco</Label>
              <Select value={databaseColumn} onValueChange={setDatabaseColumn}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma coluna" />
                </SelectTrigger>
                <SelectContent>
                  {validColumns.map((column) => (
                    <SelectItem key={column} value={column}>
                      {column}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button 
            onClick={handleSaveMapping} 
            disabled={saving || !templateName || !variableIndex || !databaseColumn || !connectionId}
            className="w-full md:w-auto"
          >
            {saving ? 'Salvando...' : 'Salvar Mapeamento'}
          </Button>
        </CardContent>
      </Card>

      {/* Lista de mapeamentos existentes */}
      <Card>
        <CardHeader>
          <CardTitle>Mapeamentos Configurados</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(groupedMappings).length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              Nenhum mapeamento configurado ainda.
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedMappings).map(([templateName, templateMappings]) => (
                <div key={templateName} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3">{templateName}</h3>
                  <div className="grid gap-2">
                    {templateMappings
                      .sort((a, b) => a.variable_index - b.variable_index)
                      .map((mapping) => (
                        <div key={mapping.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">
                              {mapping.connection_id === 0 ? `p${mapping.variable_index}` : `{{${mapping.variable_index}}}`}
                            </Badge>
                            <span className="text-sm text-muted-foreground">→</span>
                            <Badge variant="secondary">
                              {mapping.database_column}
                            </Badge>
                            {mapping.connection_id && (
                              <>
                                <span className="text-sm text-muted-foreground">|</span>
                                <Badge variant="outline">
                                  Conexão: {mapping.connection_id}
                                </Badge>
                              </>
                            )}
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => mapping.id && handleDeleteMapping(mapping.id)}
                          >
                            Excluir
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
